import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { SERVICE_CATEGORIES, HQ_STATUS } from '../../../config/ieumConfig';
import { getAllServiceRequests } from '../../../services/serviceRequestService';
import { getAllPartners, getAllRealtors } from '../../../services/memberService';
import { getAllComplaints } from '../../../services/complaintService';
import { listAuditLogsAidm } from '../../../api/aidmAuditApi';
import { formatUserName } from '../../../utils/userName';
import {
  ADMIN_REQUEST_FILTER_UNASSIGNED,
  buildAdminRequestListUrl,
  requestMatchesAdminCategory,
  requestMatchesAdminStatus,
} from '../../../utils/adminRequestFilters';

// ───── 기간 ─────
const PERIOD_TABS = [
  { key: 'today', label: '오늘' },
  { key: 'yesterday', label: '어제' },
  { key: 'week', label: '최근 7일' },
  { key: 'month', label: '이번달' },
  { key: 'prev_month', label: '전월' },
];

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const addDays = (ts, n) => ts + n * 86400000;

const inPeriod = (ts, period) => {
  if (!ts) return false;
  const now = new Date();
  const today = startOfDay(now);
  switch (period) {
    case 'today':      return ts >= today;
    case 'yesterday':  return ts >= addDays(today, -1) && ts < today;
    case 'week':       return ts >= addDays(today, -6);
    case 'month': {
      const s = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
      return ts >= s;
    }
    case 'prev_month': {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
      const e = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
      return ts >= s && ts < e;
    }
    default: return true;
  }
};

const tsOf = (r) =>
  r.created_at?.seconds ? r.created_at.seconds * 1000
  : r.createdAt?.seconds ? r.createdAt.seconds * 1000
  : typeof r.createdAt === 'string' ? new Date(r.createdAt.replace(' ', 'T')).getTime()
  : 0;

const formatWon = (n) => `₩${(n || 0).toLocaleString()}`;

// ───── 스타일 ─────
const Wrap = styled.div` max-width: 1400px; `;

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 4px;
`;
const Title = styled.h1` font-size: 18px; font-weight: 600; `;
const SubText = styled.p`
  font-size: 13px; color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 20px;
`;
const RefreshBtn = styled.button`
  font-size: 13px; color: ${({ theme }) => theme.colors.primary};
  background: none; border: none; cursor: pointer; padding: 0;
  &:hover { text-decoration: underline; }
`;

const TabRow = styled.div` display: flex; gap: 6px; margin-bottom: 20px; `;
const Tab = styled.button`
  padding: 7px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : '#fff')};
  color: ${({ $active }) => ($active ? '#fff' : '#374151')};
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  cursor: pointer;
  transition: all 0.15s;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols || 4}, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 20px;
  @media (max-width: 1024px) { grid-template-columns: repeat(2, minmax(0,1fr)); }
  @media (max-width: 600px)  { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  padding: ${({ $p }) => $p || '18px 20px'};
`;

const ClickableCard = styled(Card)`
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 1px 6px rgba(74, 108, 247, 0.1);
    transform: translateY(-1px);
  }
`;

const ClickableMiniCard = styled.div`
  background: ${({ $bg }) => $bg || '#fff'};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 1px 6px rgba(74, 108, 247, 0.1);
    transform: translateY(-1px);
  }
`;

const KPILabel = styled.div`
  font-size: 12px; color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;
const KPIValue = styled.div`
  font-size: 22px; font-weight: 600;
  color: ${({ $c, theme }) => $c || theme.colors.text};
`;
const KPISub = styled.div`
  font-size: 11px; color: ${({ theme }) => theme.colors.textLight};
  margin-top: 4px;
`;

const KpiGrid = styled(Grid)`
  @media (max-width: 600px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;

    > ${Card} {
      min-width: 0;
      padding: 14px 10px;
    }

    > ${Card}:nth-child(4) {
      grid-column: 1 / -1;
      padding: 14px 16px;
    }

    ${KPILabel} {
      font-size: 11px;
      line-height: 1.35;
    }

    ${KPIValue} {
      font-size: 19px;
      white-space: nowrap;
    }

    ${KPISub} {
      font-size: 10px;
    }
  }
`;

const SummaryGrid = styled(Grid)`
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;

    > ${ClickableCard} {
      min-width: 0;
      padding: 14px 12px;
    }

    > ${ClickableCard}:nth-child(3) {
      grid-column: 1 / -1;
    }

    ${KPIValue} {
      font-size: 18px;
      overflow-wrap: anywhere;
    }
  }
`;

const Section = styled.section` margin-bottom: 20px; `;
const SectionHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px;
`;
const SectionTitle = styled.h2` font-size: 14px; font-weight: 600; color: #1F2937; `;
const SubHint = styled.span`
  font-size: 11px; font-weight: 400;
  color: ${({ theme }) => theme.colors.textLight};
  margin-left: 6px;
`;
const MoreLink = styled.button`
  font-size: 12px; color: ${({ theme }) => theme.colors.primary};
  background: none; border: none; cursor: pointer; padding: 0;
  &:hover { text-decoration: underline; }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols || '2fr 1fr'};
  gap: 14px;
  margin-bottom: 20px;
  @media (max-width: 1024px) { grid-template-columns: 1fr; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead tr { background: #F9FAFB; }
  th {
    padding: 10px 12px;
    text-align: left;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 12px;
    white-space: nowrap;
  }
  td {
    padding: 10px 12px;
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
    color: #1F2937;
  }
  tbody tr:hover { background: #F9FAFB; }
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
  @media (max-width: 1024px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 600px)  { grid-template-columns: repeat(2, 1fr); }
`;

const StatusItem = styled.button`
  appearance: none;
  width: 100%;
  font: inherit;
  text-align: center;
  padding: 16px 10px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
    transform: translateY(-1px);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;
const StatusCount = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${({ $color }) => $color};
`;
const StatusLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 4px;
`;

const CatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0,1fr));
  gap: 10px;
  @media (max-width: 1024px) { grid-template-columns: repeat(4, 1fr); }
  @media (max-width: 600px)  { grid-template-columns: repeat(2, 1fr); }
`;
const CatItem = styled(StatusItem)``;
const CatCount = styled.div`
  font-size: 17px; font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;
const CatLabel = styled.div`
  font-size: 12px; color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 4px;
`;

const ActionGridCols = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;
const WaitingList = styled.div` display: flex; flex-direction: column; gap: 10px; `;
const WaitingRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px;
  background: #F9FAFB;
  border-radius: 8px;
  font-size: 13px;
  strong { font-size: 16px; font-weight: 700; color: #111827; }
`;
const MiniLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;
const MiniValue = styled.div`
  font-size: 18px; font-weight: 700;
  color: ${({ $c }) => $c || '#111827'};
  line-height: 1.2;
`;
const MiniSub = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 6px;
`;

const FinanceGrid = styled(Grid)`
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 14px;

    > ${ClickableMiniCard} {
      min-width: 0;
      padding: 12px;
    }

    ${MiniValue} {
      font-size: 16px;
      overflow-wrap: anywhere;
    }
  }
`;
const FlowCard = styled.div`
  margin-top: 14px;
  padding: 16px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const FlowTitle = styled.div` font-size: 13px; font-weight: 600; color: #111827; margin-bottom: 4px; `;
const FlowRow = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
`;
const FlowBox = styled.div`
  flex: 1; min-width: 160px;
  background: ${({ $bg }) => $bg || '#F9FAFB'};
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 10px 14px;
`;
const FlowBoxLabel = styled.div` font-size: 11px; color: #6B7280; `;
const FlowBoxValue = styled.div` font-size: 16px; font-weight: 700; color: #111827; margin-top: 4px; `;
const FlowBoxSub = styled.div` font-size: 11px; color: #9CA3AF; margin-top: 4px; `;
const FlowPlus = styled.div` font-size: 16px; font-weight: 700; color: #6B7280; padding: 0 4px; `;
const ProgressWrap = styled.div`
  margin-top: 14px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
`;
const ProgressHeader = styled.div`
  display: flex; justify-content: space-between;
  font-size: 13px; color: #374151;
  margin-bottom: 8px;
`;
const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #F3F4F6;
  border-radius: 999px;
  overflow: hidden;
`;
const ProgressFill = styled.div`
  height: 100%;
  background: #22C55E;
  transition: width 0.3s;
`;
const ProgressFooter = styled.div`
  display: flex; justify-content: space-between;
  margin-top: 8px;
  font-size: 12px;
  color: #6B7280;
`;
const Pill = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $c }) => ($c ? $c + '15' : '#F3F4F6')};
  color: ${({ $c }) => $c || '#6B7280'};
`;
const ButtonRow = styled.div` display: flex; gap: 6px; flex-wrap: wrap; `;
const MiniBtn = styled.button`
  padding: 6px 10px;
  background: ${({ $primary, theme }) => ($primary ? theme.colors.primary : '#fff')};
  color: ${({ $primary }) => ($primary ? '#fff' : '#374151')};
  border: 1px solid ${({ $primary, theme }) => ($primary ? theme.colors.primary : theme.colors.border)};
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;
const ActionItem = styled.button`
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }

  @media (max-width: 600px) {
    min-height: 64px;
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
    padding: 10px;
    text-align: left;
  }
`;
const ActionCount = styled.span`
  font-size: 13px; font-weight: 600;
  color: ${({ $color }) => $color};
`;

const ListRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  font-size: 13px;
  &:last-child { border-bottom: none; }
`;

const Dot = styled.span`
  display: inline-block;
  width: 6px; height: 6px; border-radius: 50%;
  background: ${({ $color }) => $color};
  margin-right: 8px;
`;

const EmptyText = styled.div`
  padding: 40px 0; text-align: center;
  color: #9CA3AF; font-size: 13px;
`;

// ───── 컴포넌트 ─────
const PIE_COLORS = ['#4A6CF7', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280'];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('month');
  const periodLabel = PERIOD_TABS.find((t) => t.key === period)?.label || '';
  const [requests, setRequests] = useState([]);
  const [partners, setPartners] = useState([]);
  const [realtors, setRealtors] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [payments, setPayments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [accountVerifs, setAccountVerifs] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [
        r, p, re, c, recvSnap, paySnap,
        asgnSnap, wdSnap, avSnap, alSnap, cmSnap, usSnap, custSnap,
      ] = await Promise.all([
        getAllServiceRequests().catch(() => []),
        getAllPartners().catch(() => []),
        getAllRealtors().catch(() => []),
        getAllComplaints().catch(() => []),
        getDocs(collection(db, 'partner_receivables')).catch(() => null),
        getDocs(collection(db, 'partner_payment_requests')).catch(() => null),
        getDocs(collection(db, 'partner_assignments')).catch(() => null),
        getDocs(collection(db, 'withdrawal_requests')).catch(() => null),
        getDocs(collection(db, 'account_verification_requests')).catch(() => null),
        listAuditLogsAidm({ limit: 100, days: 30 }).catch(() => []),
        getDocs(collection(db, 'commissions')).catch(() => null),
        getDocs(collection(db, 'users')).catch(() => null),
        getDocs(collection(db, 'customers')).catch(() => null),
      ]);
      setRequests(r);
      setPartners(p);
      setRealtors(re);
      setComplaints(c);
      setReceivables(recvSnap ? recvSnap.docs.map((d) => ({ id: d.id, ...d.data() })) : []);
      setPayments(paySnap ? paySnap.docs.map((d) => ({ id: d.id, ...d.data() })) : []);
      setAssignments(asgnSnap ? asgnSnap.docs.map((d) => ({ id: d.id, ...d.data() })) : []);
      setWithdrawals(wdSnap ? wdSnap.docs.map((d) => ({ id: d.id, ...d.data() })) : []);
      setAccountVerifs(avSnap ? avSnap.docs.map((d) => ({ id: d.id, ...d.data() })) : []);
      setAuditLogs(Array.isArray(alSnap) ? alSnap : []);
      setCommissions(cmSnap ? cmSnap.docs.map((d) => ({ id: d.id, ...d.data() })) : []);
      setUsers(usSnap ? usSnap.docs.map((d) => ({ id: d.id, ...d.data() })) : []);
      setCustomers(custSnap ? custSnap.docs.map((d) => ({ id: d.id, ...d.data() })) : []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // ── 집계 ──
  const filteredReqs = useMemo(
    () => requests.filter((r) => inPeriod(tsOf(r), period)),
    [requests, period],
  );

  const tsOfPay = (p) =>
    p.created_at?.seconds ? p.created_at.seconds * 1000
    : p.createdAt?.seconds ? p.createdAt.seconds * 1000
    : typeof p.created_at === 'string' ? new Date(p.created_at.replace(' ', 'T')).getTime()
    : typeof p.createdAt === 'string' ? new Date(p.createdAt.replace(' ', 'T')).getTime()
    : 0;
  const tsOfComm = (c) =>
    c.created_at?.seconds ? c.created_at.seconds * 1000
    : c.createdAt?.seconds ? c.createdAt.seconds * 1000
    : typeof c.created_at === 'string' ? new Date(c.created_at.replace(' ', 'T')).getTime()
    : 0;

  const kpi = useMemo(() => {
    // 누적(전체)
    const totalRequests = requests.length;
    const matchedStatuses = new Set(['assigned', 'settlement_check', 'settlement_done']);
    const totalMatched = requests.filter((r) => matchedStatuses.has(r.hqStatus)).length;
    const totalCommission = commissions.reduce((s, c) => s + (c.amount || 0), 0);

    // 미수금 합계 (전체, status !== paid/completed)
    const totalReceivable = receivables
      .filter((r) => r.status !== 'paid' && r.status !== 'completed' && r.is_paid !== true)
      .reduce((s, r) => s + (r.amount || 0), 0);

    // 총 수익 (전체)
    const totalRevenue = payments
      .filter((p) => p.status === 'completed' || p.status === 'DONE')
      .reduce((s, p) => s + (p.amount || 0), 0);

    // ── 기간(period) 기반 ──
    const periodRequests = requests.filter((r) => inPeriod(tsOf(r), period)).length;

    // 기간 정산 수납
    const periodRevenue = payments
      .filter((p) => (p.status === 'completed' || p.status === 'DONE') && inPeriod(tsOfPay(p), period))
      .reduce((s, p) => s + (p.amount || 0), 0);

    // 기간 미수 (해당 기간에 발생, 미납)
    const periodReceivable = receivables
      .filter((r) =>
        r.status !== 'paid' && r.status !== 'completed' && r.is_paid !== true
        && inPeriod(tsOfPay(r), period)
      )
      .reduce((s, r) => s + (r.amount || 0), 0);

    // 기간 수수료 (공인중개사 배정액)
    const periodCommissions = commissions
      .filter((c) => inPeriod(tsOfComm(c), period))
      .reduce((s, c) => s + (c.amount || 0), 0);

    // 공제 후 순수익 (기간)
    const periodNetProfit = periodRevenue - periodCommissions;

    // 직전 기간 비교 (전월 / 어제 등)
    const prevPeriod = period === 'today' ? 'yesterday'
      : period === 'month' ? 'prev_month'
      : null;
    const prevRevenue = prevPeriod
      ? payments
          .filter((p) => (p.status === 'completed' || p.status === 'DONE') && inPeriod(tsOfPay(p), prevPeriod))
          .reduce((s, p) => s + (p.amount || 0), 0)
      : 0;

    // 승인 대기 업체
    // 카드 카운트와 PartnerList?status=pending 페이지 결과를 일치시키기 위해
    // partners.status='pending' 만 사용 (partner_applications 컬렉션은 어디서도 처리하지 않는 legacy 영역)
    const pendingPartners = partners.filter((p) => p.status === 'pending').length;
    const pendingApprovals = pendingPartners;

    return {
      totalRequests, totalMatched, totalCommission,
      totalReceivable, totalRevenue,
      periodRequests, periodRevenue, periodReceivable, periodCommissions, periodNetProfit, prevRevenue,
      pendingApprovals,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests, partners, commissions, receivables, payments, period]);

  // 주간 트렌드 (최근 7일)
  const weeklyTrend = useMemo(() => {
    const days = [];
    const weekday = ['일','월','화','수','목','금','토'];
    const today = startOfDay(new Date());
    for (let i = 6; i >= 0; i--) {
      const ds = addDays(today, -i);
      const de = ds + 86400000;
      const count = requests.filter((r) => { const t = tsOf(r); return t >= ds && t < de; }).length;
      days.push({ day: weekday[new Date(ds).getDay()], 건수: count });
    }
    return days;
  }, [requests]);

  // 서비스별 분포 (기간 기준)
  const serviceDist = useMemo(() => {
    const m = {};
    filteredReqs.forEach((r) => { m[r.category] = (m[r.category] || 0) + 1; });
    return Object.entries(SERVICE_CATEGORIES)
      .map(([k, v]) => ({ name: v.label, value: m[k] || 0 }))
      .filter((x) => x.value > 0);
  }, [filteredReqs]);

  // 상태별 집계 (전체 누적)
  const statusBreakdown = useMemo(() => {
    const keys = ['unread', 'assigned', 'settlement_check', 'settlement_done', 'cancelled', 'hq_review_needed'];
    return keys.map((k) => ({
      key: k,
      label: HQ_STATUS[k].label,
      count: requests.filter((r) => requestMatchesAdminStatus(r, k)).length,
      color: HQ_STATUS[k].color,
    }));
  }, [requests]);

  // 미배정 상담 (카테고리별)
  const unassignedByCat = useMemo(() => {
    const unassigned = requests.filter((r) => requestMatchesAdminStatus(r, ADMIN_REQUEST_FILTER_UNASSIGNED));
    return Object.entries(SERVICE_CATEGORIES).map(([k, v]) => ({
      key: k,
      label: v.label,
      count: unassigned.filter((r) => requestMatchesAdminCategory(r, k)).length,
    }));
  }, [requests]);

  // 처리 필요 항목
  const actionNeeded = useMemo(() => {
    const unreadCount = requests.filter((r) => r.hqStatus === 'unread').length;
    const reviewNeeded = requests.filter((r) => r.hqStatus === 'hq_review_needed').length;
    const complaintPending = complaints.filter((c) => c.status === 'pending').length;
    const pendingPartners = partners.filter((p) => p.status === 'pending').length;
    const pendingRealtors = realtors.filter((r) => r.status === 'pending').length;
    const pendingWithdrawals = withdrawals.filter((w) => w.status === 'requested').length;
    return [
      { label: '상담 미배정', count: unreadCount, color: '#EF4444', to: buildAdminRequestListUrl({ status: 'unread' }) },
      { label: '본사 검토 필요', count: reviewNeeded, color: '#F97316', to: buildAdminRequestListUrl({ status: 'hq_review_needed' }) },
      { label: '미처리 민원', count: complaintPending, color: '#F59E0B', to: '/admin/service/complaints?status=pending' },
      { label: '제휴업체 승인 대기', count: pendingPartners, color: '#3B82F6', to: '/admin/members/partners?status=pending' },
      { label: '공인중개사 승인 대기', count: pendingRealtors, color: '#3B82F6', to: '/admin/members/realtors?status=pending' },
      { label: '출금 승인 대기', count: pendingWithdrawals, color: '#8B5CF6', to: '/admin/settlement/withdrawal?status=requested' },
    ];
  }, [requests, complaints, partners, realtors, withdrawals]);

  // 카테고리별 상담현황 표
  const conversionTable = useMemo(() => {
    return Object.entries(SERVICE_CATEGORIES).map(([k, v]) => {
      const list = filteredReqs.filter((r) => r.category === k);
      const done = list.filter((r) => r.hqStatus === 'settlement_done').length;
      const cancel = list.filter((r) => r.hqStatus === 'cancelled').length;
      const assign = list.filter((r) => r.hqStatus === 'assigned').length;
      const check = list.filter((r) => r.hqStatus === 'settlement_check').length;
      const pct = list.length ? Math.round((done / list.length) * 100) : 0;
      return { cat: v.label, recent: list.length, convert: done, pct, cancel, assign, check };
    });
  }, [filteredReqs]);

  // 최근 접수
  const recentReqs = useMemo(
    () => [...requests].sort((a, b) => tsOf(b) - tsOf(a)).slice(0, 5),
    [requests],
  );

  // 중개사 랭킹 (기간 내 유입 수) — O(N)
  const realtorMap = useMemo(
    () => new Map(realtors.map((r) => [r.uid, r])),
    [realtors],
  );
  const topRealtors = useMemo(() => {
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
    const m = new Map();
    requests.forEach((r) => {
      const t = tsOf(r);
      if (t < monthStart) return;
      if (r.hqStatus !== 'settlement_check' && r.hqStatus !== 'settlement_done') return;
      const id = r.source_realtor_id || r.sourceRealtorId;
      if (!id) return;
      const cur = m.get(id) || { count: 0, amount: 0 };
      cur.count += 1;
      m.set(id, cur);
    });
    // commission 수익 매핑
    commissions.forEach((c) => {
      const t = c.created_at?.seconds ? c.created_at.seconds * 1000 : 0;
      if (t < monthStart) return;
      const id = c.realtor_id;
      if (!id) return;
      const cur = m.get(id) || { count: 0, amount: 0 };
      cur.amount += (c.amount || 0);
      m.set(id, cur);
    });
    return [...m.entries()]
      .map(([id, v]) => {
        const u = realtorMap.get(id);
        return { id, name: formatUserName(u, '이름없음'), count: v.count, amount: v.amount };
      })
      .sort((a, b) => b.count - a.count || b.amount - a.amount)
      .slice(0, 5);
  }, [requests, commissions, realtorMap]);

  // 대기 현황 (처리 필요 옆 박스)
  const waitingStatus = useMemo(() => [
    { label: '출금승인 대기', count: withdrawals.filter((w) => w.status === 'requested').length },
    { label: '상담 미배정', count: requests.filter((r) => requestMatchesAdminStatus(r, ADMIN_REQUEST_FILTER_UNASSIGNED)).length },
    { label: '계좌 인증', count: accountVerifs.filter((v) => v.status === 'pending').length },
    { label: '문의 답변', count: complaints.filter((c) => c.status === 'pending' || !c.status).length },
  ], [withdrawals, requests, accountVerifs, complaints]);

  // 저평점/불만 자동 리스트업 — complaint_logs 중 최근 N건 + 제휴업체/고객/카테고리 조인
  const complaintList = useMemo(() => {
    const partnerMap = new Map();
    partners.forEach((p) => partnerMap.set(p.uid || p.id, formatUserName(p, '')));
    const customerMap = new Map();
    customers.forEach((c) => customerMap.set(c.id, c));
    const customerByNameMap = new Map();
    customers.forEach((c) => { if (c.name) customerByNameMap.set(c.name, c); });
    const requestMap = new Map();
    requests.forEach((r) => requestMap.set(r.id, r));

    return [...complaints]
      .sort((a, b) => {
        const tA = a.created_at?.seconds || a.createdAt?.seconds || 0;
        const tB = b.created_at?.seconds || b.createdAt?.seconds || 0;
        return tB - tA;
      })
      .slice(0, 6)
      .map((c) => {
        const pid = c.partnerId || c.partner_id;
        const partnerName = c.partnerName || c.partner_name || partnerMap.get(pid) || '';

        // 서비스 요청 조인 (카테고리용)
        const sr = (c.serviceRequestId && requestMap.get(c.serviceRequestId))
          || requests.find((r) => r.customer_id && customerMap.get(r.customer_id)?.name === c.customerName);
        const category = c.category || sr?.category || '';

        // 고객 조인 (연락처용)
        const cust = (c.customerId && customerMap.get(c.customerId))
          || (sr?.customer_id && customerMap.get(sr.customer_id))
          || (c.customerName && customerByNameMap.get(c.customerName));
        const phone = c.phone || c.customer_phone || cust?.phone || '';

        return { ...c, partnerName, category, phone };
      });
  }, [complaints, partners, customers, requests]);

  // 제휴업체 DB 배정 전환율
  const partnerConversion = useMemo(() => {
    return partners.slice(0, 8).map((p) => {
      const myAssigns = assignments.filter((a) => a.partner_id === p.uid);
      const reserved = myAssigns.filter((a) => a.status === 'reserved' || a.status === 'completed').length;
      const rate = myAssigns.length ? Math.round((reserved / myAssigns.length) * 100) : 0;
      const cats = Array.isArray(p.service_categories) ? p.service_categories : (p.serviceCategories || []);
      const catLabels = cats.map((c) => SERVICE_CATEGORIES[c]?.label || c).join(', ') || '-';
      return {
        uid: p.uid,
        name: formatUserName(p, '이름없음'),
        categories: catLabels,
        assigned: myAssigns.length,
        reserved,
        rate,
      };
    });
  }, [partners, assignments]);

  // 관리자 활동 로그
  const recentAuditLogs = useMemo(() => {
    return [...auditLogs]
      .sort((a, b) => (b.date?.getTime?.() || Date.parse(b.createdAt || '') || 0) - (a.date?.getTime?.() || Date.parse(a.createdAt || '') || 0))
      .slice(0, 10);
  }, [auditLogs]);

  // 사용자 역할 현황
  const roleStats = useMemo(() => {
    const s = { admin: 0, staff: 0, realtor: 0, partner: 0 };
    users.forEach((u) => {
      const role = u.role;
      if (s[role] !== undefined) s[role] += 1;
    });
    return s;
  }, [users]);

  // 미수 납부 진행률 (period 기반)
  const monthReceivableProgress = useMemo(() => {
    const inRange = payments.filter((p) => inPeriod(tsOfPay(p), period));
    const paid = inRange
      .filter((p) => p.status === 'completed' || p.status === 'DONE')
      .reduce((s, p) => s + (p.amount || 0), 0);
    const totalBilled = inRange.reduce((s, p) => s + (p.amount || 0), 0);
    const unpaid = totalBilled - paid;
    const pct = totalBilled ? Math.round((paid / totalBilled) * 100) : 0;
    return { paid, totalBilled, unpaid, pct };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, period]);

  return (
    <Wrap>
      <Header>
        <Title>운영 대시보드</Title>
        <RefreshBtn onClick={load}>{loading ? '불러오는 중...' : '새로고침'}</RefreshBtn>
      </Header>
      <SubText>이음 운영 현황을 한눈에 확인하세요.</SubText>

      <TabRow>
        {PERIOD_TABS.map((t) => (
          <Tab key={t.key} $active={period === t.key} onClick={() => setPeriod(t.key)}>{t.label}</Tab>
        ))}
      </TabRow>

      {/* 핵심 KPI: 모바일 3열 + 누적 수수료 전체 폭 */}
      <KpiGrid $cols={4}>
        <Card>
          <KPILabel>누적 요청 수</KPILabel>
          <KPIValue>{kpi.totalRequests}건</KPIValue>
          <KPISub>전체 기간</KPISub>
        </Card>
        <Card>
          <KPILabel>누적 매칭</KPILabel>
          <KPIValue $c="#8B5CF6">{kpi.totalMatched}건</KPIValue>
          <KPISub>배정 이상</KPISub>
        </Card>
        <Card>
          <KPILabel>{periodLabel} 요청</KPILabel>
          <KPIValue $c="#22C55E">{kpi.periodRequests}건</KPIValue>
          <KPISub>{periodLabel} 기준</KPISub>
        </Card>
        <Card>
          <KPILabel>누적 수수료</KPILabel>
          <KPIValue $c="#F59E0B">{formatWon(kpi.totalCommission)}</KPIValue>
          <KPISub>공인중개사 수수료 합</KPISub>
        </Card>
      </KpiGrid>

      {/* 트렌드 + 분포 */}
      <Row $cols="2fr 1fr">
        <Card>
          <SectionTitle>주간 트렌드 <SubHint>(최근 7일 기준)</SubHint></SectionTitle>
          <div style={{ marginTop: 12 }}>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={weeklyTrend}>
                <defs>
                  <linearGradient id="colorArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4A6CF7" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#4A6CF7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="건수" stroke="#4A6CF7" fill="url(#colorArea)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card>
          <SectionTitle>서비스별 분포</SectionTitle>
          {serviceDist.length === 0 ? (
            <EmptyText>데이터 없음</EmptyText>
          ) : (
            <div style={{ marginTop: 12 }}>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={serviceDist} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={2} dataKey="value">
                    {serviceDist.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </Row>

      {/* 재정 3 카드 */}
      <SummaryGrid $cols={3}>
        <ClickableCard onClick={() => navigate('/admin/settlement/partner?tab=receivables')}>
          <KPILabel>총 미수금</KPILabel>
          <KPIValue $c="#F97316">{loading ? '계산 중...' : formatWon(kpi.totalReceivable)}</KPIValue>
          <KPISub>{receivables.filter((r) => r.status !== 'completed' && r.status !== 'paid').length}건 미납</KPISub>
        </ClickableCard>
        <ClickableCard onClick={() => navigate('/admin/settlement/partner?tab=requests')}>
          <KPILabel>총 수익</KPILabel>
          <KPIValue>{loading ? '계산 중...' : formatWon(kpi.totalRevenue)}</KPIValue>
          <KPISub>{periodLabel} 정산 수납 {formatWon(kpi.periodRevenue)}</KPISub>
        </ClickableCard>
        <ClickableCard onClick={() => navigate('/admin/members/partners?status=pending')}>
          <KPILabel>승인 대기 업체</KPILabel>
          <KPIValue $c="#22C55E">{kpi.pendingApprovals}</KPIValue>
          <KPISub>제휴업체 가입요청</KPISub>
        </ClickableCard>
      </SummaryGrid>

      {/* 요청 상태별 집계 */}
      <Section>
        <SectionHeader>
          <SectionTitle>정보요청 상태별 집계 <SubHint>(전체 누적 기준)</SubHint></SectionTitle>
          <MoreLink onClick={() => navigate('/admin/service/requests')}>전체 요청 목록 →</MoreLink>
        </SectionHeader>
        <StatusGrid>
          {statusBreakdown.map((s) => (
            <StatusItem
              key={s.key}
              type="button"
              data-testid={`admin-status-filter-${s.key}`}
              aria-label={`${s.label} ${s.count}건 필터 보기`}
              title={`${s.label} 요청 보기`}
              onClick={() => navigate(buildAdminRequestListUrl({ status: s.key }))}
            >
              <StatusCount $color={s.color}>{s.count}</StatusCount>
              <StatusLabel>{s.label}</StatusLabel>
            </StatusItem>
          ))}
        </StatusGrid>
      </Section>

      {/* 미배정 상담 (카테고리별) */}
      <Section>
        <SectionHeader>
          <SectionTitle>미배정 상담 (카테고리별) <SubHint>(전체 누적 기준)</SubHint></SectionTitle>
        </SectionHeader>
        <CatGrid>
          {unassignedByCat.map((c) => (
            <CatItem
              key={c.key}
              type="button"
              data-testid={`admin-category-filter-${c.key}`}
              aria-label={`미배정 ${c.label} ${c.count}건 필터 보기`}
              title={`미배정 ${c.label} 요청 보기`}
              onClick={() => navigate(buildAdminRequestListUrl({
                status: ADMIN_REQUEST_FILTER_UNASSIGNED,
                category: c.key,
              }))}
            >
              <CatCount>{c.count}건</CatCount>
              <CatLabel>{c.label}</CatLabel>
            </CatItem>
          ))}
        </CatGrid>
      </Section>

      {/* 처리 필요 + 대기 현황 */}
      <Row $cols="2fr 1fr">
        <Card>
          <SectionHeader>
            <SectionTitle>⚠️ 처리 필요</SectionTitle>
          </SectionHeader>
          <ActionGridCols>
            {actionNeeded.map((a) => (
              <ActionItem key={a.label} onClick={() => navigate(a.to)}>
                <span>{a.label}</span>
                <ActionCount $color={a.color}>{a.count}건</ActionCount>
              </ActionItem>
            ))}
          </ActionGridCols>
        </Card>
        <Card>
          <SectionHeader>
            <SectionTitle>대기 현황</SectionTitle>
          </SectionHeader>
          <WaitingList>
            {waitingStatus.map((w) => (
              <WaitingRow key={w.label}>
                <span>{w.label}</span>
                <strong>{w.count}</strong>
              </WaitingRow>
            ))}
          </WaitingList>
        </Card>
      </Row>

      {/* 재정 요약 (period 기준) */}
      <Section>
        <SectionHeader>
          <SectionTitle>재정 요약 ({periodLabel})</SectionTitle>
          <MoreLink onClick={() => navigate('/admin/settlement/partner')}>미수금액 체크 및 결제 · 정산 관리 →</MoreLink>
        </SectionHeader>
        <SubText style={{ marginBottom: 12, marginTop: -4 }}>
          {periodLabel} 기준 · 미수금액은 수익에 포함하지 않음 · 제휴업체 정산 · 공인중개사 수수료 · 공제 후 순수익
        </SubText>
        <FinanceGrid $cols={4}>
          <ClickableMiniCard $bg="#FFF7ED" onClick={() => navigate('/admin/settlement/partner?tab=receivables')}>
            <MiniLabel>미수금 총액</MiniLabel>
            <MiniValue $c="#F97316">{formatWon(kpi.totalReceivable)}</MiniValue>
            <MiniSub>{receivables.filter((r) => r.status !== 'completed' && r.status !== 'paid').length}건 미납 · 미수 청구 →</MiniSub>
          </ClickableMiniCard>
          <ClickableMiniCard $bg="#EFF6FF" onClick={() => navigate('/admin/settlement/partner?tab=requests')}>
            <MiniLabel>{periodLabel} 정산 수납</MiniLabel>
            <MiniValue $c="#3B82F6">{formatWon(kpi.periodRevenue)}</MiniValue>
            <MiniSub>{period === 'today' ? '어제' : period === 'month' ? '전월' : '직전 기간'} {formatWon(kpi.prevRevenue)}</MiniSub>
          </ClickableMiniCard>
          <ClickableMiniCard $bg="#F5F3FF" onClick={() => navigate('/admin/settlement/commission')}>
            <MiniLabel>공인중개사 배정액 (예상)</MiniLabel>
            <MiniValue $c="#8B5CF6">{formatWon(kpi.periodCommissions)}</MiniValue>
            <MiniSub>예약완료 {commissions.filter((c) => c.type === 'conversion').length}건 기준</MiniSub>
          </ClickableMiniCard>
          <ClickableMiniCard $bg="#ECFDF5" onClick={() => navigate('/admin/settlement/withdrawal')}>
            <MiniLabel>공제 후 순수익 예상</MiniLabel>
            <MiniValue $c="#22C55E">{formatWon(kpi.periodNetProfit)}</MiniValue>
            <MiniSub>수납 − 중개사 출금 기준</MiniSub>
          </ClickableMiniCard>
        </FinanceGrid>

        <FlowCard>
          <FlowTitle>{periodLabel} 재정 흐름</FlowTitle>
          <FlowRow>
            <FlowBox $bg="#F9FAFB">
              <FlowBoxLabel>제휴업체 수납완료</FlowBoxLabel>
              <FlowBoxValue>{formatWon(kpi.periodRevenue)}</FlowBoxValue>
            </FlowBox>
            <FlowPlus>+</FlowPlus>
            <FlowBox $bg="#F5F3FF">
              <FlowBoxLabel>공인중개사 수수료 / {periodLabel} 청구액</FlowBoxLabel>
              <FlowBoxValue>{formatWon(kpi.periodCommissions)}</FlowBoxValue>
              <FlowBoxSub>
                출금완료 {formatWon(withdrawals.filter((w) => w.status === 'completed').reduce((s, w) => s + (w.amount || 0), 0))} ·
                {periodLabel}청구 {formatWon(kpi.periodCommissions)}
              </FlowBoxSub>
            </FlowBox>
            <FlowPlus>=</FlowPlus>
            <FlowBox $bg="#ECFDF5">
              <FlowBoxLabel>순수익 예상</FlowBoxLabel>
              <FlowBoxValue>{formatWon(kpi.periodNetProfit)}</FlowBoxValue>
            </FlowBox>
          </FlowRow>
          <FlowRow>
            <FlowBox $bg="#FFF7ED">
              <FlowBoxLabel>미수금 회수 예정</FlowBoxLabel>
              <FlowBoxValue>+{formatWon(kpi.totalReceivable)}</FlowBoxValue>
              <FlowBoxSub>{receivables.filter((r) => r.status !== 'completed').length}건 미납</FlowBoxSub>
            </FlowBox>
          </FlowRow>
        </FlowCard>

        <ProgressWrap>
          <ProgressHeader>
            <span>{periodLabel} 미수 납부 현황</span>
            <span style={{ color: '#22C55E', fontWeight: 600 }}>{monthReceivableProgress.pct}% 납부 완료</span>
          </ProgressHeader>
          <ProgressBar>
            <ProgressFill style={{ width: `${monthReceivableProgress.pct}%` }} />
          </ProgressBar>
          <ProgressFooter>
            <span style={{ color: '#22C55E' }}>납부 완료 {formatWon(monthReceivableProgress.paid)}</span>
            <span>총 청구 {formatWon(monthReceivableProgress.totalBilled)}</span>
            <span style={{ color: '#EF4444' }}>미납 {formatWon(monthReceivableProgress.unpaid)}</span>
          </ProgressFooter>
        </ProgressWrap>
      </Section>

      {/* 카테고리별 상담 현황 표 */}
      <Section>
        <SectionHeader>
          <SectionTitle>카테고리별 상담 현황</SectionTitle>
          <MoreLink>{PERIOD_TABS.find((t) => t.key === period)?.label}</MoreLink>
        </SectionHeader>
        <Card $p="0">
          <Table>
            <thead>
              <tr>
                <th>카테고리</th><th>접수</th><th>배정</th><th>정산 확인</th>
                <th>완료</th><th>취소</th><th>전환률</th>
              </tr>
            </thead>
            <tbody>
              {conversionTable.map((r) => (
                <tr key={r.cat}>
                  <td style={{ fontWeight: 500 }}>{r.cat}</td>
                  <td>{r.recent}</td>
                  <td>{r.assign}</td>
                  <td>{r.check}</td>
                  <td>{r.convert}</td>
                  <td>{r.cancel}</td>
                  <td><span style={{ color: '#4A6CF7', fontWeight: 500 }}>{r.pct}%</span></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      </Section>

      {/* 저평점/불만 자동 리스트업 */}
      <Section>
        <SectionHeader>
          <SectionTitle>저평점/불만 자동 리스트업</SectionTitle>
          <MoreLink onClick={() => navigate('/admin/service/complaints')}>요청 목록 →</MoreLink>
        </SectionHeader>
        <SubText style={{ marginTop: -4, marginBottom: 8 }}>진행중 취소/불만 · 불만 접수 건 자동 집계</SubText>
        <Card $p="0">
          <Table>
            <thead>
              <tr>
                <th>고객명</th><th>연락처</th><th>카테고리</th>
                <th>유형</th><th>제휴업체</th><th>신청일시</th>
              </tr>
            </thead>
            <tbody>
              {complaintList.length === 0 ? (
                <tr><td colSpan={6} style={{ color: '#9CA3AF', padding: '24px 0', textAlign: 'center' }}>데이터 없음</td></tr>
              ) : complaintList.map((c) => (
                <tr key={c.id}>
                  <td>{c.customerName || c.customer_name || '-'}</td>
                  <td>{c.phone || c.customer_phone || '-'}</td>
                  <td>{SERVICE_CATEGORIES[c.category]?.label || c.category || '-'}</td>
                  <td><Pill $c="#EF4444">● {c.type || '불만'}</Pill></td>
                  <td>{c.partnerName || c.partner_name || '-'}</td>
                  <td style={{ color: '#9CA3AF' }}>{c.createdAt || '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      </Section>

      {/* 제휴업체 저평점/불만 (자동 리스트업) */}
      <Section>
        <SectionHeader>
          <SectionTitle>제휴업체 저평점/불만 (자동 리스트업)</SectionTitle>
          <MoreLink onClick={() => navigate('/admin/members/partners')}>제휴업체 목록 →</MoreLink>
        </SectionHeader>
        <Card $p="0">
          <Table>
            <thead>
              <tr>
                <th>업체명</th><th>카테고리</th>
                <th>평균평점</th><th>불만 접수</th><th>미처리 건</th>
              </tr>
            </thead>
            <tbody>
              {partners.length === 0 ? (
                <tr><td colSpan={5} style={{ color: '#9CA3AF', padding: '24px 0', textAlign: 'center' }}>데이터 없음</td></tr>
              ) : partners.slice(0, 8).map((p) => {
                const myComplaints = complaints.filter((c) => c.partner_id === p.uid || c.partnerId === p.uid);
                const unresolved = myComplaints.filter((c) => c.status === 'pending' || !c.status).length;
                const cats = Array.isArray(p.service_categories) ? p.service_categories : (p.serviceCategories || []);
                return (
                  <tr key={p.uid}>
                    <td>{p.business_name || p.businessName || p.name || '-'}</td>
                    <td>{cats.map((c) => SERVICE_CATEGORIES[c]?.label || c).join(', ') || '-'}</td>
                    <td>{p.avg_rating ? `★ ${p.avg_rating.toFixed(1)}` : '-'}</td>
                    <td>{myComplaints.length}건</td>
                    <td style={{ color: unresolved > 0 ? '#EF4444' : '#9CA3AF' }}>{unresolved}건</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      </Section>

      {/* 제휴업체 DB 배정 전환율 */}
      <Section>
        <SectionHeader>
          <SectionTitle>제휴업체 DB 배정 전환율 % <SubHint>(보온)</SubHint></SectionTitle>
          <MoreLink onClick={() => navigate('/admin/members/partners')}>제휴업체 목록 →</MoreLink>
        </SectionHeader>
        <Card $p="0">
          <Table>
            <thead>
              <tr>
                <th>업체명</th><th>카테고리</th>
                <th>배정건수</th><th>예약완료</th><th>전환율</th>
              </tr>
            </thead>
            <tbody>
              {partnerConversion.length === 0 ? (
                <tr><td colSpan={5} style={{ color: '#9CA3AF', padding: '24px 0', textAlign: 'center' }}>데이터 없음</td></tr>
              ) : partnerConversion.map((p) => (
                <tr key={p.uid}>
                  <td>{p.name}</td>
                  <td>{p.categories}</td>
                  <td>{p.assigned}</td>
                  <td>{p.reserved}</td>
                  <td style={{ color: p.rate >= 50 ? '#22C55E' : p.rate >= 20 ? '#F59E0B' : '#EF4444', fontWeight: 600 }}>{p.rate}%</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      </Section>

      {/* 상위 중개사 랭킹 + 최근 서비스 요청 */}
      <Row $cols="1fr 1fr">
        <Card $p="0">
          <SectionHeader style={{ padding: '16px 16px 0' }}>
            <SectionTitle>상위 중개사 랭킹 (이번달 리드 전환)</SectionTitle>
            <MoreLink onClick={() => navigate('/admin/members/realtors')}>전체보기 →</MoreLink>
          </SectionHeader>
          <Table>
            <thead>
              <tr><th>업체명</th><th>전환건수</th><th>수익금</th></tr>
            </thead>
            <tbody>
              {topRealtors.length === 0 ? (
                <tr><td colSpan={3} style={{ color: '#9CA3AF', padding: '24px 0', textAlign: 'center' }}>데이터 없음</td></tr>
              ) : topRealtors.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>{r.count}건</td>
                  <td>{formatWon(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
        <Card>
          <SectionHeader>
            <SectionTitle>최근 서비스 요청</SectionTitle>
            <ButtonRow>
              <MiniBtn onClick={() => navigate('/admin/service/requests')}>신청 배정</MiniBtn>
              <MiniBtn onClick={() => navigate('/admin/notification/notices')}>공지 작성</MiniBtn>
              <MiniBtn onClick={() => navigate('/admin/service/requests')}>일괄 상태 변경</MiniBtn>
              <MiniBtn $primary onClick={() => navigate('/admin/settings/accounts')}>관리자 추가</MiniBtn>
            </ButtonRow>
          </SectionHeader>
          <MoreLink onClick={() => navigate('/admin/service/requests')} style={{ marginBottom: 8, display: 'block' }}>전체보기 →</MoreLink>
          {recentReqs.length === 0 ? (
            <EmptyText>데이터 없음</EmptyText>
          ) : (
            recentReqs.map((r) => (
              <ListRow key={r.id} onClick={() => navigate('/admin/service/requests')} style={{ cursor: 'pointer' }}>
                <span>
                  <Dot $color={HQ_STATUS[r.hqStatus]?.color || '#9CA3AF'} />
                  {r.customerName} · {r.phone || '-'} · {SERVICE_CATEGORIES[r.category]?.label || r.category}
                </span>
                <Pill $c={HQ_STATUS[r.hqStatus]?.color || '#9CA3AF'}>● {HQ_STATUS[r.hqStatus]?.label || '미배정'}</Pill>
              </ListRow>
            ))
          )}
        </Card>
      </Row>

      {/* 관리자 활동 로그 + 시스템 상태 + 사용자 역할 현황 */}
      <Row $cols="2fr 1fr">
        <Card $p="0">
          <SectionHeader style={{ padding: '16px 16px 0' }}>
            <SectionTitle>관리자 활동 로그 (최근 10건)</SectionTitle>
          </SectionHeader>
          <Table>
            <thead>
              <tr><th>일시</th><th>액션</th><th>관리자</th></tr>
            </thead>
            <tbody>
              {recentAuditLogs.length === 0 ? (
                <tr><td colSpan={3} style={{ color: '#9CA3AF', padding: '24px 0', textAlign: 'center' }}>로그 없음</td></tr>
              ) : recentAuditLogs.map((a) => (
                <tr key={a.id}>
                  <td style={{ color: '#9CA3AF' }}>{a.date ? a.date.toLocaleString('ko-KR') : '-'}</td>
                  <td>{a.summary || a.action || '-'}</td>
                  <td>{a.user || '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card>
            <SectionHeader>
              <SectionTitle>시스템 상태</SectionTitle>
            </SectionHeader>
            <p style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6 }}>
              에러율/API 응답시간은 모니터링 연동 시 표시됩니다. <br />
              <span style={{ color: '#22C55E', fontWeight: 600 }}>● 정상</span>
            </p>
          </Card>
          <Card>
            <SectionHeader>
              <SectionTitle>사용자 역할 현황</SectionTitle>
            </SectionHeader>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 8 }}>
              관리자·사용자 수는 설정에서 확인할 수 있습니다.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              <div>관리자: <strong>{roleStats.admin}</strong></div>
              <div>직원: <strong>{roleStats.staff}</strong></div>
              <div>공인중개사: <strong>{roleStats.realtor}</strong></div>
              <div>제휴업체: <strong>{roleStats.partner}</strong></div>
            </div>
            <MoreLink onClick={() => navigate('/admin/settings/accounts')} style={{ marginTop: 8, display: 'block' }}>관리자 계정 →</MoreLink>
          </Card>
        </div>
      </Row>
    </Wrap>
  );
};

export default AdminDashboard;
